import { create } from 'zustand';
import { subDays } from 'date-fns';
import type { RequestSummary, RequestDetail } from '../types';

interface DateRange {
  start: Date;
  end: Date;
  label: string;
}

interface RequestState {
  requests: RequestSummary[];
  selectedRequest: RequestDetail | null;
  isDetailOpen: boolean;
  dateRange: DateRange;
  totalRequests: number;
  isLoading: boolean;
  isLoadingDetail: boolean;

  setRequests: (requests: RequestSummary[], total: number) => void;
  setSelectedRequest: (request: RequestDetail | null) => void;
  setDetailOpen: (open: boolean) => void;
  setDateRange: (start: Date, end: Date, label: string) => void;
  setQuickRange: (days: number) => void;
  goToPreviousDay: () => void;
  goToNextDay: () => void;
  setLoading: (loading: boolean) => void;
  setLoadingDetail: (loading: boolean) => void;
  reset: () => void;
}

const getToday = () => {
  const today = new Date();
  today.setHours(23, 59, 59, 999);
  return today;
};

const getStartOfDay = (date: Date) => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
};

// Default to today only
const getDefaultRange = (): DateRange => {
  const today = new Date();
  return {
    start: getStartOfDay(today),
    end: getToday(),
    label: 'Today',
  };
};

export const useRequestStore = create<RequestState>((set) => ({
  requests: [],
  selectedRequest: null,
  isDetailOpen: false,
  dateRange: getDefaultRange(),
  totalRequests: 0,
  isLoading: false,
  isLoadingDetail: false,

  setRequests: (requests, total) =>
    set({ requests, totalRequests: total }),

  setSelectedRequest: (request) => set({ selectedRequest: request }),

  setDetailOpen: (open) => set({ isDetailOpen: open }),

  setDateRange: (start, end, label) =>
    set({ dateRange: { start: getStartOfDay(start), end, label } }),

  setQuickRange: (days) =>
    set(() => {
      const end = getToday();
      const start = getStartOfDay(subDays(end, days - 1));
      const label = days === 1 ? 'Today' : `Last ${days} days`;
      return { dateRange: { start, end, label } };
    }),

  goToPreviousDay: () =>
    set((state) => {
      const newStart = subDays(state.dateRange.start, 1);
      const newEnd = subDays(state.dateRange.end, 1);
      return {
        dateRange: {
          start: getStartOfDay(newStart),
          end: newEnd,
          label: 'Custom',
        },
      };
    }),

  goToNextDay: () =>
    set((state) => {
      const today = getToday();
      const newEnd = new Date(state.dateRange.end);
      newEnd.setDate(newEnd.getDate() + 1);
      if (newEnd > today) return state;
      const newStart = new Date(state.dateRange.start);
      newStart.setDate(newStart.getDate() + 1);
      return {
        dateRange: {
          start: getStartOfDay(newStart),
          end: newEnd,
          label: 'Custom',
        },
      };
    }),

  setLoading: (loading) => set({ isLoading: loading }),

  setLoadingDetail: (loading) => set({ isLoadingDetail: loading }),

  reset: () =>
    set({
      requests: [],
      selectedRequest: null,
      isDetailOpen: false,
      dateRange: getDefaultRange(),
      totalRequests: 0,
    }),
}));
